import {
  chronologisch,
  kantVan,
  langsteReeks,
  onderling,
  potjesVan,
  spelerStats,
  type VerloopPunt
} from './elo';
import { toonNaam } from './format';
import type { Match, Player } from './types';
import { plekken, positieStats } from './elo';

/* ==========================================================================
   Wat er uit de potjes te halen valt zodra je verder kijkt dan de ranglijst:
   rangen, vorm, records, rivaliteiten, prestaties en een eerlijke teamindeling.

   Alles hier is een pure functie over de potjes die je meegeeft. Er wordt niets
   bewaard en niets gecachet — de potjes zijn de enige bron van waarheid, en die
   staan al in het geheugen. Filter je op een poule of een seizoen, geef dan
   gewoon die deelverzameling mee.

   Let op de grens van wat we wéten: er wordt alleen een eindstand vastgelegd,
   geen doelpunten met een tijdstip. Alles wat het verloop ín een potje nodig
   heeft ("winnen na 0–5 achter") is dus niet te berekenen. Zie PRESTATIES.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Rangen — Elo is een getal, en een getal zegt weinig
   -------------------------------------------------------------------------- */

export interface Rang {
  sleutel: string;
  /** De naam zonder divisie: "Zilver". */
  familie: string;
  /** De divisie: I onderaan, III bovenaan. IJzer III is dus het hoogste ijzer. */
  divisie: 'I' | 'II' | 'III';
  /** Wat er op het scherm staat: "Zilver II". */
  label: string;
  /** Ondergrens. */
  vanaf: number;
  /** Bovengrens; bij de hoogste is dat oneindig. */
  tot: number;
  kleur: string;
}

/**
 * Zeven families van elk drie divisies, en de startrating (800) valt precies in
 * Zilver II — het midden. Iedereen begint dus op dezelfde plek en werkt zich
 * omhoog of zakt terug; niemand begint onderaan aan een lange klim.
 *
 * Een divisie is vijftig punten breed, een familie dus honderdvijftig. Met een
 * K-factor van 32 verschuift één potje je maximaal een derde divisie: genoeg om
 * beweging te zien, te weinig om in één middag van Brons naar Goud te gaan.
 *
 * Dezelfde namen als de tiers bij de prestaties, met opzet: het scheelt een
 * tweede woordenlijst. Wat ze betekenen verschilt wél — een rang zegt hoe goed
 * je bent en kan zakken, een tier zegt hoe vaak je iets deed en zakt nooit.
 */
const FAMILIES = [
  { sleutel: 'ijzer', naam: 'IJzer', kleur: '#7d7f85' },
  { sleutel: 'brons', naam: 'Brons', kleur: '#9c6644' },
  { sleutel: 'zilver', naam: 'Zilver', kleur: '#8a9199' },
  { sleutel: 'goud', naam: 'Goud', kleur: '#b5871f' },
  { sleutel: 'platinum', naam: 'Platinum', kleur: '#3f9d9d' },
  { sleutel: 'diamant', naam: 'Diamant', kleur: '#2f8fa8' },
  { sleutel: 'legende', naam: 'Legende', kleur: '#7b3fa0' }
];

/** Breedte van één divisie in ratingpunten. */
export const DIVISIE_BREEDTE = 50;

/*
 * Waar de ladder begint. Zo gekozen dat de startrating (800) precies in het
 * midden van Zilver II valt — de middelste divisie van de derde familie. Niet
 * op een grens: dan zou je eerste nederlaag je meteen degraderen.
 *
 * Onder je liggen zes divisies om in te zakken, boven je dertien om te
 * beklimmen. Dat is met opzet scheef: Elo is symmetrisch, dus de helft van de
 * spelers komt onder de start uit, maar drie families diep zakken kost je
 * driehonderd punten en dat gebeurt zelden.
 */
const LADDER_BODEM = 425;

/* Oplopend: je begint in I en klimt naar III, en daarna naar de volgende
   familie. Andersom kan ook — sommige spellen tellen af — maar omhoog tellen
   terwijl je omhoog klimt is het minste denkwerk. */
const DIVISIES: Array<'I' | 'II' | 'III'> = ['I', 'II', 'III'];

export const RANGEN: Rang[] = FAMILIES.flatMap((familie, f) =>
  DIVISIES.map((divisie, d) => {
    const stap = f * DIVISIES.length + d;
    const vanaf = LADDER_BODEM + stap * DIVISIE_BREEDTE;
    const laatste = f === FAMILIES.length - 1 && d === DIVISIES.length - 1;
    return {
      sleutel: `${familie.sleutel}-${divisie.toLowerCase()}`,
      familie: familie.naam,
      divisie,
      label: `${familie.naam} ${divisie}`,
      vanaf: stap === 0 ? 0 : vanaf,
      tot: laatste ? Infinity : vanaf + DIVISIE_BREEDTE,
      kleur: familie.kleur
    };
  })
);

export function rangVan(rating: number): Rang {
  let uit = RANGEN[0];
  for (const r of RANGEN) if (rating >= r.vanaf) uit = r;
  return uit;
}

/** Hoeveel punten tot de volgende divisie, en hoe ver ben je erdoorheen. */
export function rangVoortgang(rating: number): { rang: Rang; volgende: Rang | null; tekort: number; deel: number } {
  const rang = rangVan(rating);
  const volgende = RANGEN[RANGEN.indexOf(rang) + 1] ?? null;
  if (!volgende) return { rang, volgende: null, tekort: 0, deel: 1 };

  /* De onderste divisie loopt door tot nul; reken de balk over de gewone
     breedte, anders staat wie laag staat altijd op vol. */
  const onder = Math.max(rang.vanaf, volgende.vanaf - DIVISIE_BREEDTE);
  return {
    rang,
    volgende,
    tekort: Math.max(0, Math.ceil(volgende.vanaf - rating)),
    deel: Math.min(1, Math.max(0, (rating - onder) / (volgende.vanaf - onder)))
  };
}

/** De families, met hun bereik — voor de uitlegpagina en de grafiekbanden. */
export function rangFamilies(): { naam: string; kleur: string; vanaf: number; tot: number; divisies: Rang[] }[] {
  return FAMILIES.map((f) => {
    const divisies = RANGEN.filter((r) => r.sleutel.startsWith(`${f.sleutel}-`));
    return {
      naam: f.naam,
      kleur: f.kleur,
      vanaf: divisies[0].vanaf,
      tot: divisies[divisies.length - 1].tot,
      divisies
    };
  });
}

export interface RangVak {
  rang: Rang;
  aantal: number;
  /** Aandeel van alle spelers, 0…1. */
  deel: number;
  /** Zit de speler waar je naar kijkt in dit vak? */
  jij: boolean;
}

/**
 * Hoe de spelers over de rangen verdeeld zijn, en waar één speler daarin staat.
 * Alleen wie gespeeld heeft telt mee: iemand die op 1200 staat omdat hij nog
 * nooit aan de tafel kwam, zegt niets over het niveau.
 */
export function rangVerdeling(
  spelers: { id: string }[],
  rating: (id: string) => number,
  gespeeld: (id: string) => number,
  ik?: string
): { vakken: RangVak[]; totaal: number; mijnRang: Rang | null; mijnPlek: number; mijnPlekGedeeld: boolean } {
  const meedoen = spelers.filter((s) => gespeeld(s.id) > 0);
  const mijnRang = ik && meedoen.some((s) => s.id === ik) ? rangVan(rating(ik)) : null;

  const vakken = RANGEN.map((rang) => {
    const erin = meedoen.filter((s) => rangVan(rating(s.id)).sleutel === rang.sleutel);
    return {
      rang,
      aantal: erin.length,
      deel: meedoen.length ? erin.length / meedoen.length : 0,
      jij: Boolean(ik && erin.some((s) => s.id === ik))
    };
  });

  /* Hoeveel spelers staan er boven je — 1 is de hoogste. Een gelijke rating
     deelt de plek: twee keer 1 wordt gevolgd door 3. */
  const gesorteerd = meedoen.slice().sort((a, b) => rating(b.id) - rating(a.id));
  const nrs = plekken(gesorteerd, (s) => rating(s.id));
  const mijnIndex = ik ? gesorteerd.findIndex((s) => s.id === ik) : -1;
  const mijnPlek = mijnIndex < 0 ? 0 : nrs[mijnIndex];
  const mijnPlekGedeeld = mijnIndex >= 0 && nrs.filter((n) => n === nrs[mijnIndex]).length > 1;

  return { vakken, totaal: meedoen.length, mijnRang, mijnPlek, mijnPlekGedeeld };
}

/** De rangen als vlakken voor achter een ratinggrafiek. */
export function rangBanden(): { van: number; tot: number; kleur: string; label: string }[] {
  return RANGEN.map((r) => ({ van: r.vanaf, tot: r.tot, kleur: r.kleur, label: r.label }));
}

export type RangVerschil = { soort: 'promotie' | 'degradatie'; naar: Rang } | null;

/** Vergelijkt twee ratings van dezelfde speler; voor de melding na een potje. */
export function rangSprong(voor: number, na: number): RangVerschil {
  const a = rangVan(voor);
  const b = rangVan(na);
  if (a.sleutel === b.sleutel) return null;
  return { soort: b.vanaf > a.vanaf ? 'promotie' : 'degradatie', naar: b };
}

/* --------------------------------------------------------------------------
   Vorm — de laatste potjes als W en V
   -------------------------------------------------------------------------- */

export interface VormPotje {
  matchId: string;
  gewonnen: boolean;
  playedAt: string;
  stand: string;
}

/** Oudste eerst, zodat W-W-V-W-V van links naar rechts leest zoals de tijd. */
export function vorm(id: string, potjes: Match[], hoeveel = 5): VormPotje[] {
  return chronologisch(potjesVan(id, potjes))
    .slice(-hoeveel)
    .map((m) => {
      const k = kantVan(m, id);
      return {
        matchId: m.id,
        gewonnen: k.gewonnen,
        playedAt: m.playedAt,
        stand: `${k.eigen}–${k.ander}`
      };
    });
}

/* --------------------------------------------------------------------------
   Records en mijlpalen
   -------------------------------------------------------------------------- */

export interface Mijlpaal {
  sleutel: string;
  label: string;
  /** Het record zelf, kort genoeg voor een tegel. */
  waarde: string;
  /** Wie, en wanneer. */
  wie: string[];
  onder: string;
  ikoon: string;
}

const datumKort = (d: string | Date) =>
  new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });

/** De dag met de meeste potjes; bij gelijke stand de meest recente. */
function drukkeDag(potjes: Match[]): { dag: string; aantal: number } | null {
  const per = new Map<string, number>();
  for (const m of potjes) {
    const dag = new Date(m.playedAt).toISOString().slice(0, 10);
    per.set(dag, (per.get(dag) ?? 0) + 1);
  }
  const rijen = [...per.entries()].sort((a, b) => b[1] - a[1] || b[0].localeCompare(a[0]));
  return rijen.length ? { dag: rijen[0][0], aantal: rijen[0][1] } : null;
}

export function records(
  potjes: Match[],
  spelers: Player[],
  verloop: Record<string, VerloopPunt[]>,
  zoekSpeler: (id: string) => Player
): Mijlpaal[] {
  const uit: Mijlpaal[] = [];
  if (!potjes.length) return uit;

  /* grootste overwinning — verschil telt, en bij gelijk verschil de hoogste score */
  const grootste = potjes
    .slice()
    .sort(
      (a, b) =>
        Math.abs(b.scoreA - b.scoreB) - Math.abs(a.scoreA - a.scoreB) ||
        Math.max(b.scoreA, b.scoreB) - Math.max(a.scoreA, a.scoreB)
    )[0];
  const winnaars = grootste.scoreA > grootste.scoreB ? grootste.teamA : grootste.teamB;
  const verliezers = grootste.scoreA > grootste.scoreB ? grootste.teamB : grootste.teamA;
  uit.push({
    sleutel: 'grootste_overwinning',
    label: 'Grootste overwinning',
    waarde: `${Math.max(grootste.scoreA, grootste.scoreB)}–${Math.min(grootste.scoreA, grootste.scoreB)}`,
    wie: winnaars.map((id) => toonNaam(zoekSpeler(id))),
    onder: `tegen ${verliezers.map((id) => toonNaam(zoekSpeler(id))).join(' & ')} · ${datumKort(grootste.playedAt)}`,
    ikoon: 'knal'
  });

  /* hoogste rating ooit — het hoogste punt uit ieders verloop */
  let top: { id: string; punt: VerloopPunt } | null = null;
  for (const [id, punten] of Object.entries(verloop)) {
    for (const p of punten) if (!top || p.rating > top.punt.rating) top = { id, punt: p };
  }
  if (top) {
    uit.push({
      sleutel: 'hoogste_rating',
      label: 'Hoogste rating ooit',
      waarde: String(Math.round(top.punt.rating)),
      wie: [toonNaam(zoekSpeler(top.id))],
      onder: datumKort(top.punt.at),
      ikoon: 'stijging'
    });
  }

  /* langste winreeks aller tijden */
  const reeksen = spelers
    .map((p) => ({ speler: p, reeks: langsteReeks(p.id, potjes) }))
    .filter((r) => r.reeks > 1)
    .sort((a, b) => b.reeks - a.reeks);
  if (reeksen.length) {
    const beste = reeksen[0].reeks;
    const gedeeld = reeksen.filter((r) => r.reeks === beste);
    uit.push({
      sleutel: 'langste_reeks',
      label: 'Langste winreeks',
      waarde: `${beste}×`,
      wie: gedeeld.map((r) => r.speler.name),
      onder: gedeeld.length > 1 ? 'gedeeld record' : 'gewonnen op rij',
      ikoon: 'vlam'
    });
  }

  /* meeste potjes op één dag */
  const dag = drukkeDag(potjes);
  if (dag) {
    const dieDag = potjes.filter((m) => new Date(m.playedAt).toISOString().slice(0, 10) === dag.dag);
    const perSpeler = new Map<string, number>();
    for (const m of dieDag) {
      for (const id of [...m.teamA, ...m.teamB]) perSpeler.set(id, (perSpeler.get(id) ?? 0) + 1);
    }
    const fanatiek = [...perSpeler.entries()].sort((a, b) => b[1] - a[1])[0];
    uit.push({
      sleutel: 'drukste_dag',
      label: 'Meeste potjes op één dag',
      waarde: String(dag.aantal),
      wie: [datumKort(dag.dag)],
      onder: fanatiek ? `${toonNaam(zoekSpeler(fanatiek[0]))} speelde er ${fanatiek[1]}` : '',
      ikoon: 'kalender'
    });
  }

  /*
   * Meeste doelpunten over alles bij elkaar. In een duo telt de teamscore voor
   * beide spelers: wie welke stang bediende houdt de app niet bij, dus dat is
   * de eerlijkste maat die er is.
   */
  const doelpunten = spelers
    .map((p) => ({ speler: p, totaal: spelerStats(p.id, potjes).voor }))
    .filter((r) => r.totaal > 0)
    .sort((a, b) => b.totaal - a.totaal);
  if (doelpunten.length) {
    const meeste = doelpunten[0].totaal;
    const gedeeld = doelpunten.filter((r) => r.totaal === meeste);
    uit.push({
      sleutel: 'meeste_doelpunten',
      label: 'Meeste doelpunten',
      waarde: String(meeste),
      wie: gedeeld.map((r) => toonNaam(r.speler)),
      onder: gedeeld.length > 1 ? 'gedeeld record' : 'sinds het begin',
      ikoon: 'bal'
    });
  }

  return uit;
}

/* --------------------------------------------------------------------------
   Duo's — wie samen aan de tafel het meeste voor elkaar krijgen
   -------------------------------------------------------------------------- */

export interface Duo {
  a: string;
  b: string;
  gespeeld: number;
  gewonnen: number;
  verloren: number;
  /** Het échte winstpercentage; dat is ook wat op het scherm staat. */
  pct: number;
  /** Waarop gesorteerd wordt — zie de uitleg bij `duos()`. */
  score: number;
  voor: number;
  tegen: number;
  laatste: string | null;
}

/**
 * Alle duo's die minstens `drempel` keer samen in een 2v2 stonden, het beste
 * eerst.
 *
 * Sorteren op het kale winstpercentage zou de lijst aanvoeren met wie drie
 * keer speelde en drie keer won. Dat is geen beter duo, dat is minder bewijs.
 * Daarom telt er één gewonnen en één verloren potje bij voordat er gedeeld
 * wordt: drie uit drie wordt dan 80% en achttien uit twintig 86%, dus het paar
 * met de langste staat van dienst gaat voor. Op het scherm staat gewoon de
 * echte 100% — het getal mag kloppen, de volgorde moet eerlijk zijn.
 */
export function duos(potjes: Match[], drempel = 3): Duo[] {
  const samen = new Map<
    string,
    { a: string; b: string; gespeeld: number; gewonnen: number; voor: number; tegen: number; laatste: string }
  >();

  const tel = (paar: string[], eigen: number, anders: number, wanneer: string) => {
    if (paar.length !== 2) return;
    const [a, b] = [...paar].sort();
    const rij = samen.get(a + '|' + b) ?? { a, b, gespeeld: 0, gewonnen: 0, voor: 0, tegen: 0, laatste: wanneer };
    rij.gespeeld++;
    if (eigen > anders) rij.gewonnen++;
    rij.voor += eigen;
    rij.tegen += anders;
    if (wanneer > rij.laatste) rij.laatste = wanneer;
    samen.set(a + '|' + b, rij);
  };

  for (const m of potjes) {
    tel(m.teamA, m.scoreA, m.scoreB, m.playedAt);
    tel(m.teamB, m.scoreB, m.scoreA, m.playedAt);
  }

  return [...samen.values()]
    .filter((r) => r.gespeeld >= drempel)
    .map((r) => ({
      a: r.a,
      b: r.b,
      gespeeld: r.gespeeld,
      gewonnen: r.gewonnen,
      verloren: r.gespeeld - r.gewonnen,
      pct: (r.gewonnen / r.gespeeld) * 100,
      score: (r.gewonnen + 1) / (r.gespeeld + 2),
      voor: r.voor,
      tegen: r.tegen,
      laatste: r.laatste
    }))
    .sort((x, y) => y.score - x.score || y.gespeeld - x.gespeeld);
}

/* --------------------------------------------------------------------------
   Rivaliteiten — wie komen elkaar het vaakst tegen
   -------------------------------------------------------------------------- */

export interface Rivaliteit {
  a: string;
  b: string;
  gespeeld: number;
  gewonnenA: number;
  gewonnenB: number;
  /** 0 = volkomen in balans, 1 = eenrichtingsverkeer. */
  scheefheid: number;
  laatste: string | null;
}

/**
 * Alle paren die elkaar minstens `drempel` keer tegenkwamen, de vaakste eerst.
 * Bij een gelijk aantal wint het paar dat het dichtst bij elkaar staat — dat is
 * de confrontatie waar je naar wilt kijken.
 */
export function rivaliteiten(potjes: Match[], spelers: Player[], drempel = 4): Rivaliteit[] {
  const uit: Rivaliteit[] = [];

  for (let i = 0; i < spelers.length; i++) {
    for (let k = i + 1; k < spelers.length; k++) {
      const a = spelers[i].id;
      const b = spelers[k].id;
      const { gespeeld, gewonnenA } = onderling(a, b, potjes);
      if (gespeeld < drempel) continue;

      const samen = potjes.filter((m) => {
        const aIn = m.teamA.includes(a) ? 'A' : m.teamB.includes(a) ? 'B' : null;
        const bIn = m.teamA.includes(b) ? 'A' : m.teamB.includes(b) ? 'B' : null;
        return aIn && bIn && aIn !== bIn;
      });
      const laatste = chronologisch(samen).at(-1)?.playedAt ?? null;

      uit.push({
        a,
        b,
        gespeeld,
        gewonnenA,
        gewonnenB: gespeeld - gewonnenA,
        scheefheid: Math.abs(gewonnenA / gespeeld - 0.5) * 2,
        laatste
      });
    }
  }

  return uit.sort((x, y) => y.gespeeld - x.gespeeld || x.scheefheid - y.scheefheid);
}

/* --------------------------------------------------------------------------
   Prestaties — wat je onderweg verdient

   Elke prestatie is een teller met een doel: zo kun je hem ook laten zien als
   hij nog niet binnen is ("7 van de 10"). Wat je hier níét vindt is de
   "Comeback" uit het oorspronkelijke plan (winnen na 0–5 achter): er wordt
   alleen een eindstand vastgelegd, dus die stand van 0–5 heeft nooit bestaan in
   de gegevens. Daar is een tussenstand-invoer voor nodig; tot die tijd staat er
   "Zenuwen van staal" in de plaats — winnen als het erom spant.
   -------------------------------------------------------------------------- */

/**
 * Zeven treden per prestatie. Ze staan los van de rangen op de ranglijst: een
 * rang zegt hoe goed je bent, een tier hoe vaak je iets voor elkaar kreeg. Dat
 * zijn twee verschillende vragen, dus twee schalen.
 */
export interface Tier {
  sleutel: string;
  label: string;
  kleur: string;
}

export const TIERS: Tier[] = [
  { sleutel: 'ijzer', label: 'IJzer', kleur: '#7d7f85' },
  { sleutel: 'brons', label: 'Brons', kleur: '#9c6644' },
  { sleutel: 'zilver', label: 'Zilver', kleur: '#8a9199' },
  { sleutel: 'goud', label: 'Goud', kleur: '#b5871f' },
  { sleutel: 'platinum', label: 'Platinum', kleur: '#3f9d9d' },
  { sleutel: 'diamant', label: 'Diamant', kleur: '#2f8fa8' },
  { sleutel: 'legende', label: 'Legende', kleur: '#7b3fa0' }
];

export interface Prestatie {
  sleutel: string;
  label: string;
  /** Waar het over gaat, zonder getal — het getal verschilt per trede. */
  uitleg: string;
  /** Wat er geteld wordt, voor achter de cijfers: "12 van de 20 potjes". */
  eenheid: string;
  ikoon: string;
  /** Hoe ver je bent. */
  stand: number;
  /** Zeven drempels, oplopend; één per tier. */
  doelen: number[];
  /** -1 zolang je de eerste trede nog niet haalde. */
  tierIndex: number;
  tier: Tier | null;
  /** De eerstvolgende trede, of null als je alles hebt. */
  volgende: { tier: Tier; doel: number } | null;
  /** Voortgang binnen de huidige trede, 0…1. */
  deel: number;
  /**
   * Hoe vol de balk staat: je stand gedeeld door het eerstvolgende doel. Bij
   * 1 van de 3 is dat een derde, bij 38 van de 50 driekwart. Bewust niet
   * hetzelfde als `deel`, dat binnen de trede meet en dus terugspringt naar nul
   * zodra je er een haalt.
   */
  balk: number;
  behaald: boolean;
}

/** Zoekt bij een stand de hoogste trede die gehaald is. */
function metTier(p: Omit<Prestatie, 'tierIndex' | 'tier' | 'volgende' | 'deel' | 'balk' | 'behaald'>): Prestatie {
  let tierIndex = -1;
  for (let i = 0; i < p.doelen.length; i++) if (p.stand >= p.doelen[i]) tierIndex = i;

  const volgendeIndex = tierIndex + 1;
  const volgende =
    volgendeIndex < p.doelen.length
      ? { tier: TIERS[volgendeIndex], doel: p.doelen[volgendeIndex] }
      : null;

  const vanaf = tierIndex >= 0 ? p.doelen[tierIndex] : 0;
  const deel = volgende ? Math.min(1, Math.max(0, (p.stand - vanaf) / (volgende.doel - vanaf))) : 1;

  return {
    ...p,
    tierIndex,
    tier: tierIndex >= 0 ? TIERS[tierIndex] : null,
    volgende,
    deel,
    balk: volgende ? Math.min(1, Math.max(0, p.stand / volgende.doel)) : 1,
    behaald: tierIndex >= 0
  };
}

/**
 * Meeste potjes in één kalenderweek, maandag tot en met zondag. Niet een
 * schuivend venster van zeven dagen: "deze week" is wat mensen bedoelen, en
 * een reeks die over een weekend heen loopt hoort twee weken te zijn.
 */
function drukstWeek(eigen: Match[]): number {
  const perWeek = new Map<string, number>();
  for (const m of eigen) {
    const d = new Date(m.playedAt);
    /* Naar de maandag van die week; getDay() geeft zondag als 0. */
    const maandag = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    maandag.setDate(maandag.getDate() - ((maandag.getDay() + 6) % 7));
    const sleutel = maandag.toISOString().slice(0, 10);
    perWeek.set(sleutel, (perWeek.get(sleutel) ?? 0) + 1);
  }
  return Math.max(0, ...perWeek.values());
}

/** Grootste stijging binnen dertig dagen; het klimwerk van één maand. */
function besteKlim(punten: VerloopPunt[]): number {
  let beste = 0;
  for (let i = 0; i < punten.length; i++) {
    const grens = new Date(punten[i].at).getTime() - 30 * 86400000;
    let laagste = punten[i].rating;
    for (let k = i; k >= 0 && new Date(punten[k].at).getTime() >= grens; k--) {
      laagste = Math.min(laagste, punten[k].rating);
    }
    beste = Math.max(beste, punten[i].rating - laagste);
  }
  return Math.round(beste);
}

export function prestaties(id: string, potjes: Match[], verloop: VerloopPunt[] = []): Prestatie[] {
  const eigen = potjesVan(id, potjes);
  const s = spelerStats(id, potjes);

  let afgangen = 0;
  let nipt = 0;
  let kansloosVerloren = 0;
  let kansloosGewonnen = 0;
  const metPloeggenoot = new Map<string, number>();

  for (const m of eigen) {
    const k = kantVan(m, id);
    if (k.gewonnen && k.ander === 0) afgangen++;
    if (k.gewonnen && k.eigen - k.ander === 1) nipt++;

    /* Onder de drie blijven is de grens; nul telt mee, want met 10-0 van tafel
       geveegd worden is daar de zuiverste vorm van. */
    if (!k.gewonnen && k.eigen < 3) kansloosVerloren++;
    if (k.gewonnen && k.ander < 3) kansloosGewonnen++;

    if (k.gewonnen) {
      for (const p of k.ploeggenoten) metPloeggenoot.set(p, (metPloeggenoot.get(p) ?? 0) + 1);
    }
  }

  const besteDuo = [...metPloeggenoot.values()].reduce((a, b) => Math.max(a, b), 0);

  /* Achterin en voorin tellen apart; alleen 2v2 kent een opstelling. */
  const pos = positieStats(id, potjes);

  /*
   * De drempels lopen bewust ongelijk op. Iets wat vanzelf gebeurt als je maar
   * lang genoeg speelt (Marathon) moet ver doorlopen; iets wat zeldzaam is
   * (Onverslaanbaar) mag eerder klaar zijn, anders is Legende onhaalbaar.
   */
  return [
    {
      sleutel: 'afgang',
      label: 'Afgang',
      uitleg: 'Potjes gewonnen zonder ook maar één tegendoelpunt.',
      eenheid: 'afgangen',
      ikoon: 'bezem',
      stand: afgangen,
      doelen: [1, 3, 6, 10, 20, 35, 50]
    },
    {
      sleutel: 'zenuwen',
      label: 'Zenuwen van staal',
      uitleg: 'Gewonnen met precies één doelpunt verschil.',
      eenheid: 'thrillers',
      ikoon: 'hart',
      stand: nipt,
      doelen: [1, 3, 5, 10, 20, 35, 50]
    },
    {
      sleutel: 'veelvraat',
      label: 'Veelvraat',
      uitleg: 'Meeste potjes binnen één week.',
      eenheid: 'potjes in een week',
      ikoon: 'bord',
      stand: drukstWeek(eigen),
      doelen: [5, 10, 15, 20, 25, 35, 50]
    },
    {
      sleutel: 'onverslaanbaar',
      label: 'Onverslaanbaar',
      uitleg: 'Langste reeks gewonnen potjes op rij.',
      eenheid: 'op rij',
      ikoon: 'schild',
      stand: langsteReeks(id, potjes),
      doelen: [3, 5, 8, 10, 15, 20, 25]
    },
    {
      sleutel: 'marathon',
      label: 'Marathon',
      uitleg: 'Potjes gespeeld, gewonnen of verloren.',
      eenheid: 'potjes',
      ikoon: 'loper',
      stand: s.gespeeld,
      doelen: [10, 25, 50, 100, 250, 500, 1000]
    },
    {
      sleutel: 'klimmer',
      label: 'Klimmer',
      uitleg: 'Grootste stijging binnen één maand.',
      eenheid: 'punten in een maand',
      ikoon: 'berg',
      stand: besteKlim(verloop),
      doelen: [25, 50, 75, 100, 150, 200, 300]
    },
    {
      sleutel: 'duivelskoppel',
      label: 'Duivelskoppel',
      uitleg: 'Gewonnen potjes met dezelfde ploeggenoot.',
      eenheid: 'samen gewonnen',
      ikoon: 'duo',
      stand: besteDuo,
      doelen: [3, 5, 10, 20, 35, 50, 75]
    },
    {
      sleutel: 'onder_de_tafel',
      label: 'Onder de tafel door',
      uitleg: 'Potjes verloren waarin je zelf onder de drie doelpunten bleef.',
      eenheid: 'keer weggespeeld',
      ikoon: 'verbod',
      stand: kansloosVerloren,
      doelen: [1, 3, 6, 10, 20, 35, 50]
    },
    {
      sleutel: 'kruipen',
      label: 'Kruipen zal je',
      uitleg: 'Potjes gewonnen waarin de tegenpartij onder de drie doelpunten bleef.',
      eenheid: 'keer weggespeeld',
      ikoon: 'knal',
      stand: kansloosGewonnen,
      doelen: [1, 3, 6, 10, 20, 35, 50]
    },
    {
      sleutel: 'sluitpost',
      label: 'Sluitpost',
      uitleg: 'Potjes gewonnen terwijl je achterin stond.',
      eenheid: 'als verdediger',
      ikoon: 'verbod',
      stand: pos.verdediger.gewonnen,
      doelen: [3, 5, 10, 20, 35, 50, 75]
    },
    {
      sleutel: 'afmaker',
      label: 'Afmaker',
      uitleg: 'Potjes gewonnen terwijl je voorin stond.',
      eenheid: 'als aanvaller',
      ikoon: 'knal',
      stand: pos.aanvaller.gewonnen,
      doelen: [3, 5, 10, 20, 35, 50, 75]
    },
    {
      /*
       * De laagste van de twee, niet de som: zo kom je alleen vooruit door
       * op allebei de plekken te winnen. Wie altijd voorin staat blijft hier
       * op nul, hoeveel hij daar ook wint.
       */
      sleutel: 'tweebenig',
      label: 'Tweebenig',
      uitleg: 'Gewonnen op beide plekken; de zwakste van de twee telt.',
      eenheid: 'op elke plek',
      ikoon: 'weegschaal',
      stand: Math.min(pos.verdediger.gewonnen, pos.aanvaller.gewonnen),
      doelen: [2, 5, 10, 20, 30, 45, 60]
    },
  ].map(metTier);
}

/* --------------------------------------------------------------------------
   Eerlijke teamverdeler
   -------------------------------------------------------------------------- */

export interface Indeling {
  teamA: [string, string];
  teamB: [string, string];
  ratingA: number;
  ratingB: number;
  /** Verschil in teamgemiddelde; kleiner is eerlijker. */
  verschil: number;
  /** Winstkans van team A volgens Elo, in procenten. */
  kansA: number;
}

/**
 * Vier spelers zijn op precies drie manieren in twee duo's te delen. We
 * berekenen ze alle drie en zetten de eerlijkste vooraan — geen slim algoritme
 * nodig, en zo zie je meteen wat de alternatieven waren.
 */
export function verdeelTeams(vier: string[], rating: (id: string) => number): Indeling[] {
  if (vier.length !== 4) return [];
  const [w, x, y, z] = vier;
  const paren: [[string, string], [string, string]][] = [
    [
      [w, x],
      [y, z]
    ],
    [
      [w, y],
      [x, z]
    ],
    [
      [w, z],
      [x, y]
    ]
  ];

  return paren
    .map(([a, b]) => {
      const ratingA = (rating(a[0]) + rating(a[1])) / 2;
      const ratingB = (rating(b[0]) + rating(b[1])) / 2;
      /* Dezelfde formule als in de Elo-berekening zelf. */
      const kansA = 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
      return {
        teamA: a,
        teamB: b,
        ratingA,
        ratingB,
        verschil: Math.abs(ratingA - ratingB),
        kansA: kansA * 100
      };
    })
    .sort((a, b) => a.verschil - b.verschil);
}

/* --------------------------------------------------------------------------
   Niveau — wat je verdient door te blijven spelen

   Rating zegt hoe goed je bent en gaat omlaag als je verliest. Een niveau doet
   het omgekeerde: het zegt hoeveel je hebt gedaan, en het zakt nooit. Zo heeft
   ook wie zelden wint iets om naartoe te werken.

   Net als de rest is dit een pure functie over de potjes. Er wordt geen XP
   bewaard: bewaarde punten en herrekende potjes lopen vroeg of laat uit de pas,
   en dan klopt er niets meer van.
   -------------------------------------------------------------------------- */

/** Wat een potje en een trede opleveren. */
export const XP = {
  gespeeld: 10,
  gewonnen: 15,
  /** Per trede, oplopend: IJzer levert het minst op, Legende het meest. */
  tier: [20, 40, 70, 110, 160, 220, 300]
};

/** Wat je bij elkaar moet spelen om level `n` te halen. */
export function xpVoorLevel(level: number): number {
  if (level <= 1) return 0;
  const n = level - 1;
  return 100 * n + 50 * ((n * (n - 1)) / 2);
}

export interface NiveauBron {
  label: string;
  xp: number;
}

export interface Niveau {
  level: number;
  xp: number;
  /** Hoeveel XP je in dit level al hebt, en hoeveel het er kost. */
  inLevel: number;
  levelKost: number;
  deel: number;
  /** Waar het vandaan komt, voor de uitklap op het profiel. */
  bronnen: NiveauBron[];
}

export function niveauVan(id: string, potjes: Match[], verloop: VerloopPunt[] = []): Niveau {
  const s = spelerStats(id, potjes);
  const uitPotjes = s.gespeeld * XP.gespeeld;
  const uitWinst = s.gewonnen * XP.gewonnen;

  /* Elke gehaalde trede telt mee, niet alleen de hoogste. */
  const uitPrestaties = prestaties(id, potjes, verloop).reduce(
    (som, p) => som + XP.tier.slice(0, p.tierIndex + 1).reduce((a, b) => a + b, 0),
    0
  );

  const xp = uitPotjes + uitWinst + uitPrestaties;

  let level = 1;
  while (xpVoorLevel(level + 1) <= xp) level++;

  const vanaf = xpVoorLevel(level);
  const tot = xpVoorLevel(level + 1);

  return {
    level,
    xp,
    inLevel: xp - vanaf,
    levelKost: tot - vanaf,
    deel: Math.min(1, Math.max(0, (xp - vanaf) / (tot - vanaf))),
    bronnen: [
      { label: `${s.gespeeld} potjes gespeeld`, xp: uitPotjes },
      { label: `${s.gewonnen} keer gewonnen`, xp: uitWinst },
      { label: 'prestaties', xp: uitPrestaties }
    ]
  };
}


/* ---------- verdeling per familie ---------- */

export interface FamilieVak {
  naam: string;
  kleur: string;
  aantal: number;
  /** Aandeel van alle spelers, 0…1. */
  deel: number;
  /** Zit de speler waar je naar kijkt in deze familie? */
  jij: boolean;
}

/**
 * Dezelfde telling als rangVerdeling, maar samengevouwen tot Zilver, Goud,
 * Platinum enzovoort. Eenentwintig staafjes uitsplitsen naar I, II en III leest
 * niet: bij een handvol spelers staat er overal nul en valt er niets te zien.
 */
export function familieVerdeling(
  spelers: { id: string }[],
  rating: (id: string) => number,
  gespeeld: (id: string) => number,
  ik?: string
): { vakken: FamilieVak[]; totaal: number; mijnRang: Rang | null; mijnPlek: number; mijnPlekGedeeld: boolean } {
  const per = rangVerdeling(spelers, rating, gespeeld, ik);

  const vakken = rangFamilies().map((f) => {
    const sleutels = new Set(f.divisies.map((d) => d.sleutel));
    const erin = per.vakken.filter((v) => sleutels.has(v.rang.sleutel));
    const aantal = erin.reduce((n, v) => n + v.aantal, 0);
    return {
      naam: f.naam,
      kleur: f.kleur,
      aantal,
      deel: per.totaal ? aantal / per.totaal : 0,
      jij: erin.some((v) => v.jij)
    };
  });

  return { ...per, vakken };
}

/* --------------------------------------------------------------------------
   Potjes genereren uit een selectie

   Je vinkt aan wie er meedoen en de app maakt er hele potjes van. Zeven man
   levert twee potjes op: acht plekken, dus één iemand speelt twee keer. Vijf
   man levert één potje op en iemand kijkt toe.

   Wie er dubbel speelt of op de bank zit wordt niet geloot maar gekozen op
   aantal gespeelde potjes: wie het minst heeft gespeeld gaat twee keer, wie
   het meest heeft gespeeld zit een ronde uit. Zo loopt het over een middag
   vanzelf glad.
   -------------------------------------------------------------------------- */

export interface Generatie {
  potjes: Indeling[];
  /** Wie er twee keer speelt. */
  dubbel: string[];
  /** Wie deze ronde toekijkt. */
  bank: string[];
  /** Waarom het er zoveel zijn — voor onder de knop. */
  uitleg: string;
}

/** Hoeveel hele potjes je van dit aantal spelers kunt maken. */
export function aantalPotjesVoor(spelers: number): number {
  if (spelers < 4) return 0;
  return Math.max(1, Math.round(spelers / 4));
}

export function genereerPotjes(
  gekozen: string[],
  rating: (id: string) => number,
  gespeeld: (id: string) => number,
  aantal?: number,
  /* Zuiver loten in plaats van op sterkte verdelen. Voor als het niet om de
     ranglijst gaat maar om een potje dat gewoon anders moet lopen. */
  willekeurig = false
): Generatie {
  const n = gekozen.length;
  if (n < 4) {
    return { potjes: [], dubbel: [], bank: [], uitleg: 'Er zijn minstens vier spelers nodig voor één potje.' };
  }

  /* Nooit meer potjes dan waarvoor iedereen hooguit twee keer hoeft. */
  const gevraagd = aantal ?? aantalPotjesVoor(n);
  const potjesTal = Math.max(1, Math.min(gevraagd, Math.floor((n * 2) / 4)));
  const plekken = potjesTal * 4;

  /* Minst gespeeld eerst: die mogen dubbel. Meest gespeeld achteraan: die
     zitten als eerste een ronde uit. */
  const opDrukte = gekozen
    .slice()
    .sort((a, b) => gespeeld(a) - gespeeld(b) || rating(b) - rating(a) || (a < b ? -1 : 1));

  const dubbel = plekken > n ? opDrukte.slice(0, plekken - n) : [];
  const bank = plekken < n ? opDrukte.slice(plekken - n) : [];
  const meedoen = gekozen.filter((id) => !bank.includes(id));

  /* Eén regel per plek aan tafel; wie dubbel speelt staat er twee keer in. */
  const stoelen = [...meedoen, ...dubbel];
  if (willekeurig) {
    for (let i = stoelen.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [stoelen[i], stoelen[j]] = [stoelen[j], stoelen[i]];
    }
  } else {
    stoelen.sort((a, b) => rating(b) - rating(a));
  }

  /* Slangsgewijs verdelen: sterkste naar potje 1, dan terug, zodat de potjes
     even zwaar worden in plaats van dat alle sterken bij elkaar komen. */
  const groepen: string[][] = Array.from({ length: potjesTal }, () => []);
  stoelen.forEach((id, i) => {
    const ronde = Math.floor(i / potjesTal);
    const kolom = i % potjesTal;
    const g = ronde % 2 === 0 ? kolom : potjesTal - 1 - kolom;
    groepen[g].push(id);
  });

  /* Niemand kan tegen zichzelf spelen. Staat iemand twee keer in hetzelfde
     potje, ruil hem dan met iemand uit een ander potje die daar niet zit. */
  for (const groep of groepen) {
    for (let i = 0; i < groep.length; i++) {
      if (groep.indexOf(groep[i]) === i) continue;
      const ruil = groepen.find((a) => a !== groep && !a.includes(groep[i]) && a.some((x) => !groep.includes(x)));
      if (!ruil) continue;
      const k = ruil.findIndex((x) => !groep.includes(x));
      [groep[i], ruil[k]] = [ruil[k], groep[i]];
    }
  }

  const potjes = groepen
    .filter((g) => g.length === 4)
    /* Op sterkte: de eerlijkste van de drie mogelijke indelingen. Geloot: neem
       de volgorde zoals hij viel, anders sluipt de rating er alsnog in. */
    .map((g) =>
      willekeurig
        ? verdeelTeams(g, rating).find(
            (i) => i.teamA.includes(g[0]) && i.teamA.includes(g[1])
          ) ?? verdeelTeams(g, rating)[0]
        : verdeelTeams(g, rating)[0]
    )
    .filter(Boolean);

  const stukjes: string[] = [
    `${potjes.length} ${potjes.length === 1 ? 'potje' : 'potjes'} uit ${n} spelers`,
    willekeurig ? 'geloot' : 'op sterkte verdeeld'
  ];
  if (dubbel.length) stukjes.push(`${dubbel.length} ${dubbel.length === 1 ? 'speler speelt' : 'spelers spelen'} twee keer`);
  if (bank.length) stukjes.push(`${bank.length} ${bank.length === 1 ? 'speler kijkt' : 'spelers kijken'} toe`);

  return { potjes, dubbel, bank, uitleg: stukjes.join(' · ') };
}

/**
 * Koppelt spelers tot duo's voor een toernooi. Sterkste met zwakste, tweede met
 * op-een-na-zwakste, enzovoort: zo liggen de duo's zo dicht mogelijk bij
 * elkaar en wordt het schema niet op voorhand beslist.
 *
 * Met `willekeurig` gaat de rating er juist uit — voor wie liever gewoon loot.
 * De lijst moet een even aantal spelers bevatten.
 */
export function maakDuos(
  ids: string[],
  rating: (id: string) => number,
  willekeurig = false
): string[][] {
  const rij = ids.slice();
  if (willekeurig) {
    for (let i = rij.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rij[i], rij[j]] = [rij[j], rij[i]];
    }
  } else {
    rij.sort((a, b) => rating(b) - rating(a));
  }

  const duos: string[][] = [];
  for (let i = 0; i < Math.floor(rij.length / 2); i++) {
    duos.push(willekeurig ? [rij[i * 2], rij[i * 2 + 1]] : [rij[i], rij[rij.length - 1 - i]]);
  }
  return duos;
}
